import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { usePageTitle } from "../../hooks/usePageTitle";

interface OrderItem {
  product_name: string;
  product_slug: string;
  quantity: number;
  price: number;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  shipping_address: string;
  shipping_city: string;
  shipping_state: string;
  shipping_zip: string;
  subtotal: number;
  tax: number;
  shipping_cost: number;
  total: number;
  status: string;
  payment_status: string;
  payment_intent_id: string | null;
  created_at: string;
  items: OrderItem[];
}

export default function OrderDetail() {
  usePageTitle("Order Details");
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetail();
    }
  }, [orderNumber]);

  const fetchOrderDetail = async () => {
    try {
      const response = await fetch(`/api/customer/orders/${orderNumber}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setOrder(data);
      } else if (response.status === 401) {
        setError("Please log in to view this order");
      } else if (response.status === 403) {
        setError("You don't have permission to view this order");
      } else {
        setError("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      setError("An error occurred while loading the order");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusTimeline = (status: string) => {
    const statuses = ["processing", "shipped", "delivered"];
    const currentIndex = statuses.indexOf(status.toLowerCase());

    return statuses.map((s, index) => ({
      name: s.charAt(0).toUpperCase() + s.slice(1),
      completed: index <= currentIndex,
      current: index === currentIndex,
    }));
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">Loading order details...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="card text-center">
          <p className="text-red-600 mb-4">{error || "Order not found"}</p>
          <Link to="/account" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Order Details</h1>
          <p className="text-gray-600">Order #{order.order_number}</p>
        </div>
        <Link to="/account" className="btn-secondary">
          Back to Dashboard
        </Link>
      </div>

      {/* Order Status Timeline */}
      {order.status.toLowerCase() !== "cancelled" && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-6">Order Status</h2>
          <div className="flex items-center justify-between">
            {getStatusTimeline(order.status).map((step, index) => (
              <div key={step.name} className="flex-1 relative">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step.completed
                        ? "bg-black text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {step.completed ? "✓" : index + 1}
                  </div>
                  <p className={`mt-2 text-sm ${step.current ? "font-bold" : ""}`}>
                    {step.name}
                  </p>
                </div>
                {index < getStatusTimeline(order.status).length - 1 && (
                  <div
                    className={`absolute top-5 left-1/2 w-full h-0.5 ${
                      step.completed ? "bg-black" : "bg-gray-200"
                    }`}
                    style={{ zIndex: -1 }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancelled Status */}
      {order.status.toLowerCase() === "cancelled" && (
        <div className="card mb-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-800 flex items-center justify-center">
              ✕
            </div>
            <div>
              <h3 className="font-bold text-red-800">Order Cancelled</h3>
              <p className="text-sm text-red-600">This order has been cancelled</p>
            </div>
          </div>
        </div>
      )}

      {/* Order Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Customer Information */}
        <div className="card">
          <h2 className="text-lg font-bold mb-4">Customer Information</h2>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Name:</span> {order.customer_name}
            </p>
            <p>
              <span className="font-medium">Email:</span> {order.customer_email}
            </p>
            <p>
              <span className="font-medium">Phone:</span> {order.customer_phone}
            </p>
          </div>
        </div>

        {/* Shipping Address */}
        <div className="card">
          <h2 className="text-lg font-bold mb-4">Shipping Address</h2>
          <div className="text-sm">
            <p>{order.shipping_address}</p>
            <p>
              {order.shipping_city}, {order.shipping_state} {order.shipping_zip}
            </p>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="card mb-6">
        <h2 className="text-xl font-bold mb-4">Order Items</h2>
        <div className="space-y-4">
          {order.items.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-4 border-b last:border-b-0"
            >
              <div className="flex-1">
                <Link
                  to={`/product/${item.product_slug}`}
                  className="font-medium hover:underline"
                >
                  {item.product_name}
                </Link>
                <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatCurrency(item.price * item.quantity)}</p>
                <p className="text-sm text-gray-600">
                  {formatCurrency(item.price)} each
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Summary */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Order Summary</h2>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Tax</span>
            <span>{formatCurrency(order.tax)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Shipping</span>
            <span>{formatCurrency(order.shipping_cost)}</span>
          </div>
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
          </div>
          <div className="flex justify-between text-sm pt-2">
            <span className="text-gray-600">Payment Status</span>
            <span
              className={`px-2 py-1 rounded text-xs ${
                order.payment_status === "paid"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {order.payment_status}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Order Date</span>
            <span>{formatDate(order.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Need Help Section */}
      <div className="card mt-6 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold mb-1">Need Help?</h3>
            <p className="text-sm text-gray-600">
              Contact us about this order
            </p>
          </div>
          <Link
            to="/contact"
            state={{ orderNumber: order.order_number }}
            className="btn-primary"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
